import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

/**
 * Reálná mapa nad OpenStreetMap daty. Body kreslíme jako circleMarker, ne jako
 * klasické pin ikony — Leaflet by pro ně chtěl PNG soubory ze svého balíčku,
 * které se při buildu přes Vite nedohledají a mapa by zůstala bez značek.
 */
export interface MapPoint {
  id: string
  name: string
  city: string
  lat: number
  lng: number
  /** Název CSS proměnné, např. 'signal-good'. Rozbalí se na skutečnou barvu. */
  colorVar: string
  /** Poloměr bodu v pixelech. */
  radius: number
  /** Text v bublině po najetí. */
  badge?: string
  /** 'dot' = kroužek pobočky, 'pin' = jednotlivá špendlíková značka. */
  variant?: 'dot' | 'pin'
  /** Číslo vypsané přímo do značky (počet objednávek). */
  label?: string
}

/** Jednorázový efekt na daném místě — pobočka „bouchne" a vyletí objednávka. */
export interface MapBurst {
  id: string
  lat: number
  lng: number
  /** Popisek, který vyletí nahoru (např. název pobočky). */
  label?: string
}

interface Props {
  points: MapPoint[]
  /** Když je true, body krátce pulzují — používá se u živé mapy objednávek. */
  pulse?: boolean
  /** Efekty přehrané při nové objednávce. Každý se po doběhnutí sám odstraní. */
  bursts?: MapBurst[]
  className?: string
}

// Musí odpovídat délce animace v globals.css (.om-burst).
const BURST_MS = 1800

// Hranice Česka. Místo pevného zoomu mapu dopočítáváme přes fitBounds, aby
// republika vyplnila kontejner nezávisle na jeho poměru stran — na široké
// TV i v úzkém sloupci dashboardu.
const CZ_BOUNDS: [[number, number], [number, number]] = [
  [48.55, 12.09],
  [51.06, 18.86],
]
const CZ_CENTER: [number, number] = [49.82, 15.47]

// Decentní podklad, který nepřebije data na dashboardu. Světlá i tmavá
// varianta podle aktivního motivu.
//
// Původně CARTO, ale ten od října 2026 bez API klíče vrací místo mapy
// dlaždici s nápisem "API KEY REQUIRED". Esri Canvas je zdarma a bez klíče.
// Pozor na pořadí {z}/{y}/{x} — je obrácené oproti CARTO, a nemá ani
// subdoménu {s}, ani retina {r}.
const TILES = {
  light:
    'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
  dark:
    'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
}
const ATTRIBUTION = '&copy; OpenStreetMap &copy; Esri'

function currentTheme(): 'light' | 'dark' {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'
}

function cssColor(name: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim()
  return v || '#888888'
}

export function OsmMap({ points, pulse = false, bursts = [], className }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const tileRef = useRef<L.TileLayer | null>(null)
  const layerRef = useRef<L.LayerGroup | null>(null)
  const burstLayerRef = useRef<L.LayerGroup | null>(null)
  // Které efekty už proběhly — aby se při každém překreslení nespouštěly znovu.
  const playedRef = useRef<Set<string>>(new Set())

  // Mapu vytvoříme jednou; překreslování bodů běží v samostatném efektu níž.
  useEffect(() => {
    if (!hostRef.current || mapRef.current) return

    const map = L.map(hostRef.current, {
      center: CZ_CENTER,
      zoom: 7,
      zoomControl: false,
      attributionControl: true,
      // Bez tohohle Leaflet zaokrouhluje fitBounds dolů na celý stupeň zoomu
      // a mapa pak ukazuje i notný kus sousedních států.
      zoomSnap: 0,
      zoomDelta: 0.25,
      // Dashboard běží bez obsluhy na TV, takže veškeré ovládání vypínáme.
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      touchZoom: false,
    })

    tileRef.current = L.tileLayer(TILES[currentTheme()], {
      attribution: ATTRIBUTION,
      maxZoom: 12,
      minZoom: 5,
    }).addTo(map)

    layerRef.current = L.layerGroup().addTo(map)
    burstLayerRef.current = L.layerGroup().addTo(map)
    mapRef.current = map

    // Dotáhnout na Česko hned po vytvoření i po každé změně velikosti.
    const fitCz = () => map.fitBounds(CZ_BOUNDS, { padding: [4, 4], animate: false })
    fitCz()

    // Přepnutí světlý/tmavý motiv vyměníme jen dlaždice, mapu nestavíme znovu.
    const observer = new MutationObserver(() => {
      if (!tileRef.current) return
      tileRef.current.setUrl(TILES[currentTheme()])
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })

    // Kontejner na dashboardu mění velikost při přepínání slidů.
    const resize = new ResizeObserver(() => {
      map.invalidateSize()
      fitCz()
    })
    resize.observe(hostRef.current)

    return () => {
      observer.disconnect()
      resize.disconnect()
      map.remove()
      mapRef.current = null
      tileRef.current = null
      layerRef.current = null
      burstLayerRef.current = null
    }
  }, [])

  // Efekty při nové objednávce. Marker se po doběhnutí animace sám odstraní,
  // takže se v mapě nehromadí.
  useEffect(() => {
    const layer = burstLayerRef.current
    if (!layer) return
    for (const b of bursts) {
      if (playedRef.current.has(b.id)) continue
      playedRef.current.add(b.id)

      const icon = L.divIcon({
        className: 'om-burst-wrap',
        html:
          `<span class="om-burst">` +
          `<span class="om-burst__ring"></span>` +
          `<span class="om-burst__core"></span>` +
          `<span class="om-burst__parcel"></span>` +
          (b.label ? `<span class="om-burst__label">${b.label}</span>` : '') +
          `</span>`,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      })
      const marker = L.marker([b.lat, b.lng], { icon, interactive: false, keyboard: false })
      marker.addTo(layer)
      window.setTimeout(() => {
        layer.removeLayer(marker)
      }, BURST_MS)
    }

    // Ať sada nepřetéká při dlouhém běhu na televizi.
    if (playedRef.current.size > 500) playedRef.current = new Set()
  }, [bursts])

  useEffect(() => {
    const layer = layerRef.current
    if (!layer) return
    layer.clearLayers()

    for (const p of points) {
      const color = cssColor(p.colorVar)
      const size = p.radius * 2

      // Vlastní značka místo výchozí Leaflet ikony: kroužek s měkkou září
      // v barvách značky. Píše se jako HTML, takže se dá stylovat z CSS
      // a nepotřebuje žádné obrázky z balíčku Leafletu.
      const icon = L.divIcon({
        className: 'om-marker-wrap',
        html:
          `<span class="om-marker${pulse ? ' om-marker--pulse' : ''}${
            p.variant === 'pin' ? ' om-marker--pin' : ''
          }" style="--om-color:${color};--om-size:${size}px">` +
          `<span class="om-marker__halo"></span>` +
          `<span class="om-marker__core"></span>` +
          (p.label ? `<span class="om-marker__label">${p.label}</span>` : '') +
          `</span>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      })

      L.marker([p.lat, p.lng], { icon, keyboard: false })
        .bindTooltip(
          `<strong>${p.name}</strong><br/>${p.city}${p.badge ? `<br/>${p.badge}` : ''}`,
          { direction: 'top', opacity: 0.95, offset: [0, -size / 2] },
        )
        .addTo(layer)
    }
  }, [points, pulse])

  return <div ref={hostRef} className={className} aria-label="Mapa poboček" />
}
